import { signal } from '@preact/signals';
import type { Clerk as ClerkConstructor } from '@clerk/clerk-js/no-rhc';
import { ApiClient } from '../api/client';
import { ApiError } from '../api/errors';
import { MeApi } from '../api/meApi';
import type { ProfileResponse } from '../contracts/viewer';
import { appEnv } from '../app/env';
import {
  nextProfileFailure,
  type AuthStatus,
  type ViewerProfileStatus,
} from './profileState';

type ClerkClient = InstanceType<typeof ClerkConstructor>;

export type { AuthStatus, ViewerProfileStatus } from './profileState';

export interface AuthUser {
  id: string;
  label: string;
  imageUrl: string | null;
}

export const authStatusState = signal<AuthStatus>(appEnv.clerkPublishableKey ? 'loading' : 'disabled');
export const authErrorState = signal<string | null>(null);
export const authUserState = signal<AuthUser | null>(null);
export const viewerProfileState = signal<ProfileResponse | null>(null);
export const viewerProfileStatusState = signal<ViewerProfileStatus>('idle');
export const viewerProfileErrorState = signal<string | null>(null);
export const legalGateOpenState = signal(false);

let clerkInstance: ClerkClient | null = null;
let clerkPromise: Promise<ClerkClient | null> | null = null;
let unsubscribeClerk: (() => void) | null = null;

function userLabel(user: ClerkClient['user']): string {
  if (!user) return 'Signed in';
  const primaryEmail = user.primaryEmailAddress?.emailAddress;
  const fullName = user.fullName;
  return fullName || primaryEmail || user.username || user.id;
}

function syncClerkState(clerk: ClerkClient): void {
  authErrorState.value = null;
  if (clerk.session && clerk.user) {
    authStatusState.value = 'signed_in';
    authUserState.value = {
      id: clerk.user.id,
      label: userLabel(clerk.user),
      imageUrl: clerk.user.imageUrl || null,
    };
    return;
  }

  authStatusState.value = clerk.loaded ? 'signed_out' : 'loading';
  authUserState.value = null;
  viewerProfileState.value = null;
  viewerProfileStatusState.value = 'idle';
  viewerProfileErrorState.value = null;
  legalGateOpenState.value = false;
}

export async function initializeAuth(): Promise<void> {
  await getClerk();
}

export async function getClerk(): Promise<ClerkClient | null> {
  if (!appEnv.clerkPublishableKey) {
    authStatusState.value = 'disabled';
    return null;
  }
  if (clerkInstance) return clerkInstance;
  if (!clerkPromise) {
    clerkPromise = loadClerk();
  }
  return clerkPromise;
}

async function loadClerk(): Promise<ClerkClient | null> {
  try {
    const { Clerk } = await import('@clerk/clerk-js/no-rhc');
    const clerk = new Clerk(appEnv.clerkPublishableKey);
    clerkInstance = clerk;
    authStatusState.value = 'loading';
    await clerk.load();
    unsubscribeClerk?.();
    unsubscribeClerk = clerk.addListener(() => syncClerkState(clerk));
    syncClerkState(clerk);
    return clerk;
  } catch (error) {
    clerkInstance = null;
    clerkPromise = null;
    authStatusState.value = 'error';
    authErrorState.value = error instanceof Error ? error.message : 'Failed to initialize Clerk';
    return null;
  }
}

export async function getAuthToken(): Promise<string | null> {
  const clerk = await getClerk();
  if (!clerk?.session) return null;
  return clerk.session.getToken();
}

export function createMeApi(): MeApi {
  return new MeApi(new ApiClient({ baseUrl: appEnv.apiBaseUrl, getAuthToken }));
}

export async function openSignIn(): Promise<void> {
  const clerk = await getClerk();
  clerk?.openSignIn();
}

export async function openAccountPortal(): Promise<void> {
  const clerk = await getClerk();
  clerk?.openUserProfile();
}

export async function signOut(): Promise<void> {
  const clerk = await getClerk();
  await clerk?.signOut();
  syncSignedOutState();
}

export function syncSignedOutState(): void {
  authStatusState.value = appEnv.clerkPublishableKey ? 'signed_out' : 'disabled';
  authUserState.value = null;
  viewerProfileState.value = null;
  viewerProfileStatusState.value = 'idle';
  viewerProfileErrorState.value = null;
  legalGateOpenState.value = false;
}

export async function refreshViewerProfile(signal?: AbortSignal): Promise<void> {
  if (authStatusState.value !== 'signed_in') return;
  viewerProfileStatusState.value = 'loading';
  viewerProfileErrorState.value = null;

  try {
    const profile = await createMeApi().getProfile(signal);
    if (signal?.aborted) return;
    viewerProfileState.value = profile;
    legalGateOpenState.value = !profile.profile.legal_acceptance_current;
    viewerProfileStatusState.value = profile.profile.legal_acceptance_current ? 'ready' : 'legal_required';
  } catch (error) {
    if (signal?.aborted) return;
    const failure = nextProfileFailure(error);
    if (failure.clearProfile) viewerProfileState.value = null;
    legalGateOpenState.value = failure.openLegalGate;
    viewerProfileErrorState.value = failure.message;
    viewerProfileStatusState.value = failure.status;
  }
}

export async function acceptCurrentLegal(signal?: AbortSignal): Promise<void> {
  const profile = viewerProfileState.value;
  if (!profile) return;

  viewerProfileStatusState.value = 'loading';
  viewerProfileErrorState.value = null;
  try {
    await createMeApi().acceptLegal(
      {
        terms_version: profile.legal.terms_version,
        privacy_version: profile.legal.privacy_version,
        ip_hash: null,
        user_agent_hash: null,
      },
      signal,
    );
    await refreshViewerProfile(signal);
  } catch (error) {
    if (signal?.aborted) return;
    if (error instanceof ApiError && error.code === 'stale_legal_version') {
      viewerProfileErrorState.value = 'Legal terms changed while this page was open. Refreshing profile.';
      await refreshViewerProfile(signal);
      return;
    }
    viewerProfileErrorState.value = error instanceof Error ? error.message : 'Legal acceptance failed';
    viewerProfileStatusState.value = 'error';
  }
}
